/**
 * ClaimReview — post-session full-width claim review view.
 * Batch approve/reject with section grouping.
 * Supports inline editing of pending claims before approval.
 */

import { useState } from 'preact/hooks';
// ClaimReview groups claims by section prefix for batch review — simple grouping
// since these are single-session pending claims, no cross-session index collision risk.

interface Claim {
	id: string;
	section: string;
	field: string;
	value: unknown;
	confidence: 'stated' | 'inferred';
	timestamp: number;
	agentReasoning?: string;
	narrative?: string;
}

interface Props {
	pendingClaims: Claim[];
	approvedClaims: Claim[];
	onClaimAction: (claimId: string, action: 'approve' | 'reject', editedValue?: unknown) => void;
	onBulkAction: (action: 'approve' | 'reject', claimIds: string[]) => Promise<void>;
	onBack: () => void;
}

function formatClaimValue(claim: Claim): string {
	if (claim.narrative) return claim.narrative;
	const { value } = claim;
	if (typeof value === 'string') return value;
	if (Array.isArray(value)) {
		return value.map(item => {
			if (typeof item === 'string') return item;
			if (typeof item === 'object' && item !== null) {
				const obj = item as Record<string, unknown>;
				if (obj.name) {
					const parts = [String(obj.name)];
					if (obj.context) parts.push(String(obj.context));
					else if (obj.depth) parts.push(String(obj.depth));
					return parts.join(' — ');
				}
				if (obj.label) return String(obj.label);
				if (obj.url) return String(obj.url);
			}
			return String(item);
		}).join(', ');
	}
	if (typeof value === 'object' && value !== null) {
		return Object.entries(value as Record<string, unknown>)
			.filter(([, v]) => v !== null && v !== undefined && v !== '')
			.map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`)
			.join('\n');
	}
	return String(value);
}

function formatValueForEdit(value: unknown): string {
	if (typeof value === 'string') return value;
	return JSON.stringify(value, null, 2);
}

function parseEditedValue(raw: string): unknown {
	try {
		return JSON.parse(raw);
	} catch {
		return raw;
	}
}

function ClaimRow({ claim, isPending, onAction, editingId, onStartEdit, onSaveEdit, onCancelEdit, editValue, onEditValueChange }: {
	claim: Claim;
	isPending: boolean;
	onAction: (claimId: string, action: 'approve' | 'reject', editedValue?: unknown) => void;
	editingId: string | null;
	onStartEdit: (claim: Claim) => void;
	onSaveEdit: (claimId: string) => void;
	onCancelEdit: () => void;
	editValue: string;
	onEditValueChange: (value: string) => void;
}) {
	// Skip header fields absorbed into group headers
	if (['company', 'title', 'period'].includes(claim.field) &&
		claim.section.match(/^experience\.work_history\[\d+\]$/)) {
		return null;
	}

	// Skip internal enrichment data
	if (claim.field === 'beta_enrichment') return null;

	// v2: Show project context for claims within work_history[N].projects[M]
	const projMatch = claim.section.match(/\.projects\[(\d+)\]/);
	const projPrefix = projMatch ? `proj ${parseInt(projMatch[1]) + 1} · ` : '';
	const label = projPrefix + claim.field.replace(/_/g, ' ');
	const content = formatClaimValue(claim);
	const isMultiline = content.includes('\n');
	const isEditing = editingId === claim.id;

	return (
		<div class={`cr-row ${isPending ? 'cr-row--pending' : 'cr-row--approved'} ${isEditing ? 'cr-row--editing' : ''}`}>
			<div class="cr-row-content">
				<span class="cr-row-label">{label}</span>
				{isEditing ? (
					<div class="cr-edit">
						<textarea
							class="cr-edit-input"
							value={editValue}
							onInput={(e) => onEditValueChange((e.target as HTMLTextAreaElement).value)}
							rows={Math.max(3, editValue.split('\n').length + 1)}
						/>
						<div class="cr-edit-actions">
							<button
								class="btn btn-primary btn-xs"
								onClick={() => onSaveEdit(claim.id)}
							>
								Save & Approve
							</button>
							<button
								class="btn btn-ghost btn-xs"
								onClick={onCancelEdit}
							>
								Cancel
							</button>
						</div>
					</div>
				) : isMultiline ? (
					<div class="cr-row-value cr-row-value--multi">
						{content.split('\n').map((line, i) => <p key={i}>{line}</p>)}
					</div>
				) : (
					<span class="cr-row-value">{content}</span>
				)}
			</div>
			{isPending && !isEditing && (
				<div class="cr-row-actions">
					<button
						class="cr-action cr-action--edit"
						onClick={() => onStartEdit(claim)}
						title="Edit before approving"
					>&#9998;</button>
					<button
						class="cr-action cr-action--approve"
						onClick={() => onAction(claim.id, 'approve')}
						title="Approve"
					>&#10003;</button>
					<button
						class="cr-action cr-action--reject"
						onClick={() => onAction(claim.id, 'reject')}
						title="Reject"
					>&#10005;</button>
				</div>
			)}
			{!isPending && (
				<span class="cr-row-status">&#10003;</span>
			)}
		</div>
	);
}

export function ClaimReview({ pendingClaims, approvedClaims, onClaimAction, onBulkAction, onBack }: Props) {
	const [bulkLoading, setBulkLoading] = useState(false);
	const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
	const [editingClaim, setEditingClaim] = useState<string | null>(null);
	const [editValue, setEditValue] = useState('');

	function handleStartEdit(claim: Claim) {
		setEditingClaim(claim.id);
		setEditValue(formatValueForEdit(claim.value));
	}

	function handleSaveEdit(claimId: string) {
		const parsed = parseEditedValue(editValue);
		onClaimAction(claimId, 'approve', parsed);
		setEditingClaim(null);
		setEditValue('');
	}

	function handleCancelEdit() {
		setEditingClaim(null);
		setEditValue('');
	}

	const totalPending = pendingClaims.length;
	const totalApproved = approvedClaims.length;
	const pendingIds = new Set(pendingClaims.map(c => c.id));
	const allClaims = [...pendingClaims, ...approvedClaims];

	// Simple section-based grouping for batch review (single session, no cross-session risk)
	const identityClaims = allClaims.filter(c => c.section.startsWith('identity'));
	const skillClaims = allClaims.filter(c => c.section.startsWith('skills'));
	const projectClaims = allClaims.filter(c => c.section.startsWith('projects'));
	const peopleClaims = allClaims.filter(c => c.section.startsWith('people'));
	const eventsClaims = allClaims.filter(c => c.section.startsWith('events'));

	// Group work claims by index for structured display (single session — safe)
	const workByIdx = new Map<number, { company: string; title: string; period: string; claims: Claim[] }>();
	for (const c of allClaims.filter(c => c.section.startsWith('experience.work_history'))) {
		const idxMatch = c.section.match(/work_history\[(\d+)\]/);
		if (!idxMatch) continue;
		const idx = parseInt(idxMatch[1]);
		if (!workByIdx.has(idx)) workByIdx.set(idx, { company: '', title: '', period: '', claims: [] });
		const group = workByIdx.get(idx)!;
		if (c.field === 'company' && !c.section.includes('.projects[')) group.company = typeof c.value === 'string' ? c.value : '';
		if (c.field === 'title' && !c.section.includes('.projects[')) group.title = typeof c.value === 'string' ? c.value : '';
		if (c.field === 'period' && !c.section.includes('.projects[')) group.period = typeof c.value === 'string' ? c.value : '';
		group.claims.push(c);
	}
	const work = [...workByIdx.values()].filter(g => g.company || g.claims.length > 0);

	async function handleApproveAll() {
		if (bulkLoading || totalPending === 0) return;
		setBulkLoading(true);
		try {
			await onBulkAction('approve', pendingClaims.map(c => c.id));
		} finally {
			setBulkLoading(false);
		}
	}

	async function handleApproveSectionClaims(claims: Claim[]) {
		const ids = claims.filter(c => pendingIds.has(c.id)).map(c => c.id);
		if (ids.length === 0 || bulkLoading) return;
		setBulkLoading(true);
		try {
			await onBulkAction('approve', ids);
		} finally {
			setBulkLoading(false);
		}
	}

	function toggleSection(key: string) {
		setCollapsedSections(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}

	function sectionPendingCount(claims: Claim[]): number {
		return claims.filter(c => pendingIds.has(c.id)).length;
	}

	return (
		<div class="claim-review">
			<div class="cr-header">
				<div class="cr-header-left">
					<h2 class="cr-title">Review Session Claims</h2>
					<p class="cr-subtitle">
						{totalApproved} approved
						{totalPending > 0 && ` · ${totalPending} pending`}
					</p>
				</div>
				{totalPending > 0 && (
					<button
						class="btn btn-primary btn-sm"
						onClick={handleApproveAll}
						disabled={bulkLoading}
					>
						{bulkLoading ? 'Processing...' : `Approve All ${totalPending}`}
					</button>
				)}
			</div>

			<div class="cr-sections">
				{/* Identity */}
				{identityClaims.length > 0 && (
					<div class="cr-section">
						<div class="cr-section-header" onClick={() => toggleSection('identity')}>
							<span class="cr-section-chevron" style={{ transform: collapsedSections.has('identity') ? 'rotate(-90deg)' : 'none' }}>▾</span>
							<span class="cr-section-label">Identity & Positioning</span>
							<span class="cr-section-count">{identityClaims.length}</span>
							{sectionPendingCount(identityClaims) > 0 && (
								<button
									class="btn btn-ghost btn-xs cr-section-approve"
									onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(identityClaims); }}
									disabled={bulkLoading}
								>
									✓ All {sectionPendingCount(identityClaims)}
								</button>
							)}
						</div>
						{!collapsedSections.has('identity') && (
							<div class="cr-section-body">
								{identityClaims.map(c => (
									<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
								))}
							</div>
						)}
					</div>
				)}

				{/* Work History */}
				{work.map((group, i) => {
					const key = `work-${i}`;
					const allWorkClaims = group.claims;
					const pending = sectionPendingCount(allWorkClaims);
					return (
						<div key={key} class="cr-section">
							<div class="cr-section-header" onClick={() => toggleSection(key)}>
								<span class="cr-section-chevron" style={{ transform: collapsedSections.has(key) ? 'rotate(-90deg)' : 'none' }}>▾</span>
								<span class="cr-section-label">
									{group.company}
									{group.title && <span class="cr-section-role"> — {group.title}</span>}
								</span>
								{group.period && <span class="cr-section-period">{group.period}</span>}
								<span class="cr-section-count">{allWorkClaims.length}</span>
								{pending > 0 && (
									<button
										class="btn btn-ghost btn-xs cr-section-approve"
										onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(allWorkClaims); }}
										disabled={bulkLoading}
									>
										✓ All {pending}
									</button>
								)}
							</div>
							{!collapsedSections.has(key) && (
								<div class="cr-section-body">
									{allWorkClaims.map(c => (
										<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
									))}
								</div>
							)}
						</div>
					);
				})}

				{/* Projects */}
				{projectClaims.length > 0 && (
					<div class="cr-section">
						<div class="cr-section-header" onClick={() => toggleSection('projects')}>
							<span class="cr-section-chevron" style={{ transform: collapsedSections.has('projects') ? 'rotate(-90deg)' : 'none' }}>▾</span>
							<span class="cr-section-label">Projects</span>
							<span class="cr-section-count">{projectClaims.length}</span>
							{sectionPendingCount(projectClaims) > 0 && (
								<button
									class="btn btn-ghost btn-xs cr-section-approve"
									onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(projectClaims); }}
									disabled={bulkLoading}
								>
									✓ All {sectionPendingCount(projectClaims)}
								</button>
							)}
						</div>
						{!collapsedSections.has('projects') && (
							<div class="cr-section-body">
								{projectClaims.map(c => (
									<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
								))}
							</div>
						)}
					</div>
				)}

				{/* People (v2) */}
				{peopleClaims.length > 0 && (
					<div class="cr-section">
						<div class="cr-section-header" onClick={() => toggleSection('people')}>
							<span class="cr-section-chevron" style={{ transform: collapsedSections.has('people') ? 'rotate(-90deg)' : 'none' }}>▾</span>
							<span class="cr-section-label">People</span>
							<span class="cr-section-count">{peopleClaims.length}</span>
							{sectionPendingCount(peopleClaims) > 0 && (
								<button
									class="btn btn-ghost btn-xs cr-section-approve"
									onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(peopleClaims); }}
									disabled={bulkLoading}
								>
									✓ All {sectionPendingCount(peopleClaims)}
								</button>
							)}
						</div>
						{!collapsedSections.has('people') && (
							<div class="cr-section-body">
								{peopleClaims.map(c => (
									<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
								))}
							</div>
						)}
					</div>
				)}

				{/* Events (v2) */}
				{eventsClaims.length > 0 && (
					<div class="cr-section">
						<div class="cr-section-header" onClick={() => toggleSection('events')}>
							<span class="cr-section-chevron" style={{ transform: collapsedSections.has('events') ? 'rotate(-90deg)' : 'none' }}>▾</span>
							<span class="cr-section-label">Events</span>
							<span class="cr-section-count">{eventsClaims.length}</span>
							{sectionPendingCount(eventsClaims) > 0 && (
								<button
									class="btn btn-ghost btn-xs cr-section-approve"
									onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(eventsClaims); }}
									disabled={bulkLoading}
								>
									✓ All {sectionPendingCount(eventsClaims)}
								</button>
							)}
						</div>
						{!collapsedSections.has('events') && (
							<div class="cr-section-body">
								{eventsClaims.map(c => (
									<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
								))}
							</div>
						)}
					</div>
				)}

				{/* Skills */}
				{skillClaims.length > 0 && (
					<div class="cr-section">
						<div class="cr-section-header" onClick={() => toggleSection('skills')}>
							<span class="cr-section-chevron" style={{ transform: collapsedSections.has('skills') ? 'rotate(-90deg)' : 'none' }}>▾</span>
							<span class="cr-section-label">Skills</span>
							<span class="cr-section-count">{skillClaims.length}</span>
							{sectionPendingCount(skillClaims) > 0 && (
								<button
									class="btn btn-ghost btn-xs cr-section-approve"
									onClick={(e) => { e.stopPropagation(); handleApproveSectionClaims(skillClaims); }}
									disabled={bulkLoading}
								>
									✓ All {sectionPendingCount(skillClaims)}
								</button>
							)}
						</div>
						{!collapsedSections.has('skills') && (
							<div class="cr-section-body">
								{skillClaims.map(c => (
									<ClaimRow key={c.id} claim={c} isPending={pendingIds.has(c.id)} onAction={onClaimAction} editingId={editingClaim} onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} editValue={editValue} onEditValueChange={setEditValue} />
								))}
							</div>
						)}
					</div>
				)}
			</div>

			<div class="cr-footer">
				<button class="btn btn-ghost btn-sm" onClick={onBack}>
					Back to Chat
				</button>
			</div>

			<style>{claimReviewStyles}</style>
		</div>
	);
}

const claimReviewStyles = `
	.claim-review {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.cr-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		padding: 1.25rem 1.5rem;
		border-bottom: 1px solid var(--color-border);
		flex-shrink: 0;
	}

	.cr-header-left {
		flex: 1;
	}

	.cr-title {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0;
	}

	.cr-subtitle {
		font-size: 0.75rem;
		color: var(--color-text-muted);
		margin-top: 0.25rem;
	}

	.cr-sections {
		flex: 1;
		overflow-y: auto;
		padding: 0.5rem 0;
	}

	.cr-section {
		border-bottom: 1px solid var(--color-border);
	}

	.cr-section:last-child {
		border-bottom: none;
	}

	.cr-section-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.75rem 1.5rem;
		cursor: pointer;
		user-select: none;
		transition: background 150ms;
	}

	.cr-section-header:hover {
		background: var(--color-bg-elevated);
	}

	.cr-section-chevron {
		font-size: 0.75rem;
		color: var(--color-text-muted);
		transition: transform 150ms;
		flex-shrink: 0;
		width: 1rem;
		text-align: center;
	}

	.cr-section-label {
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--color-text);
		flex: 1;
	}

	.cr-section-role {
		font-weight: 400;
		color: var(--color-text-muted);
	}

	.cr-section-period {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		white-space: nowrap;
	}

	.cr-section-count {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		background: var(--color-bg-elevated);
		padding: 0.125rem 0.375rem;
		border-radius: 2px;
	}

	.cr-section-approve {
		font-size: 0.6875rem;
		white-space: nowrap;
	}

	.cr-section-body {
		padding: 0 1.5rem 0.75rem 2.5rem;
	}

	/* Rows */
	.cr-row {
		display: flex;
		align-items: flex-start;
		gap: 0.5rem;
		padding: 0.375rem 0 0.375rem 0.625rem;
		border-left: 2px solid transparent;
		margin: 0.125rem 0;
	}

	.cr-row--pending {
		border-left-color: var(--color-primary);
	}

	.cr-row--approved {
		border-left-color: var(--color-approve);
	}

	.cr-row-content {
		flex: 1;
		min-width: 0;
	}

	.cr-row-label {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		margin-right: 0.375rem;
	}

	.cr-row-value {
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--color-text);
	}

	.cr-row-value--multi p {
		font-size: 0.8125rem;
		line-height: 1.5;
		color: var(--color-text);
		margin: 0;
		padding-left: 0.25rem;
	}

	.cr-row-status {
		color: var(--color-approve);
		font-size: 0.75rem;
		flex-shrink: 0;
		width: 1.5rem;
		text-align: center;
	}

	.cr-row-actions {
		display: flex;
		gap: 0.25rem;
		flex-shrink: 0;
	}

	.cr-action {
		width: 1.5rem;
		height: 1.5rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text-muted);
		font-size: 0.75rem;
		cursor: pointer;
		transition: all 150ms;
		padding: 0;
	}

	.cr-action--approve:hover {
		background: var(--color-approve);
		border-color: var(--color-approve);
		color: white;
	}

	.cr-action--edit:hover {
		background: var(--color-primary);
		border-color: var(--color-primary);
		color: white;
	}

	.cr-action--reject:hover {
		background: var(--color-reject);
		border-color: var(--color-reject);
		color: white;
	}

	/* Editing */
	.cr-row--editing {
		border-left-color: var(--color-primary);
		background: var(--color-bg-elevated);
		padding: 0.5rem 0.625rem;
		border-radius: 2px;
	}

	.cr-edit {
		margin-top: 0.25rem;
	}

	.cr-edit-input {
		width: 100%;
		font-family: var(--font-mono);
		font-size: 0.8125rem;
		line-height: 1.5;
		padding: 0.5rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text);
		resize: vertical;
		box-sizing: border-box;
	}

	.cr-edit-input:focus {
		outline: none;
		border-color: var(--color-primary);
	}

	.cr-edit-actions {
		display: flex;
		gap: 0.375rem;
		margin-top: 0.375rem;
		justify-content: flex-end;
	}

	.cr-footer {
		padding: 0.75rem 1.5rem;
		border-top: 1px solid var(--color-border);
		flex-shrink: 0;
	}

	.btn-xs {
		font-size: 0.6875rem;
		padding: 0.125rem 0.5rem;
	}
`;
